import React from 'react';
import { Box, ButtonBase, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Snapshot, SnapshotMeta } from './lib/types';
import { fmtIct } from './lib/ict';
import { FONT, GOLD_SOFT, GOLD_UNDERLINE, HEADER_GRADIENT, ON_DARK_TEXT, RADIUS } from './lib/brand';

/** The dark "Data as of ..." bar. Clicking it opens "Data sources & freshness". */
export function AsOfBar({ snapshot, onOpen, readOnly = true }: { snapshot: Snapshot; onOpen: () => void; readOnly?: boolean }) {
  return (
    <ButtonBase
      onClick={onOpen}
      title="Data sources and freshness"
      data-testid="as-of-bar"
      sx={{
        width: '100%', background: HEADER_GRADIENT, color: ON_DARK_TEXT, px: 2, py: 1, fontSize: 12, textAlign: 'center', display: 'block', borderRadius: RADIUS.md, mb: 2,
        position: 'relative', overflow: 'hidden',
        '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '2px', background: GOLD_UNDERLINE },
      }}
    >
      Data as of <b>{fmtIct(snapshot.generated_ict)}</b>, compiled from bot files in <span style={{ fontFamily: FONT.mono, color: GOLD_SOFT }}>{snapshot.source_root}</span> (status/, recommendations/, logs/, MASTER-LEDGER){readOnly ? ' · read-only' : ''}
    </ButtonBase>
  );
}

const row = (k: string, v: React.ReactNode) => (
  <Box key={k} sx={{ display: 'flex', gap: 2, py: 0.6, borderBottom: '1px solid', borderColor: 'divider', fontSize: 13 }}>
    <Box sx={{ width: 170, flexShrink: 0, color: 'text.secondary' }}>{k}</Box>
    <Box sx={{ minWidth: 0, wordBreak: 'break-word', fontFamily: typeof v === 'string' ? FONT.mono : undefined }}>{v}</Box>
  </Box>
);

export function SourcesDialog({ open, onClose, snapshot, meta }: { open: boolean; onClose: () => void; snapshot: Snapshot; meta: SnapshotMeta | null }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="bo-sources-title">
      <DialogTitle id="bo-sources-title" sx={{ fontFamily: FONT.heading, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Data sources &amp; freshness
        <IconButton aria-label="Close" onClick={onClose} size="small">✕</IconButton>
      </DialogTitle>
      <DialogContent>
        {row('Snapshot generated', fmtIct(snapshot.generated_ict))}
        {row('Received by admin', meta ? fmtIct(meta.received_at) : 'no data')}
        {row('Delivered by', meta ? (meta.source === 'seed' ? 'seed (loaded by hand, not from Grok Bot)' : 'Grok Bot, through S3') : 'no data')}
        {row('Schema version', snapshot.schema_version)}
        {row('Snapshot id (sha256)', meta ? meta.sha256.slice(0, 16) : 'no data')}
        {row('Source root', snapshot.source_root)}
        {row('Ledger', snapshot.ledger_prov ? `${snapshot.ledger_prov.path} · ${fmtIct(snapshot.ledger_prov.mtime_ict)}` : 'Not stated in source')}
        {row('Active window', `${snapshot.active_window_hours} h`)}
        {row('Bots', String(snapshot.bots.length))}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Warnings ({snapshot.warnings.length})</Typography>
        {snapshot.warnings.length === 0
          ? <Typography variant="body2" color="text.secondary">None.</Typography>
          : snapshot.warnings.map((w) => <Box key={w} sx={{ fontSize: 12, fontFamily: FONT.mono, mb: 0.5 }}>{w}</Box>)}
      </DialogContent>
    </Dialog>
  );
}
